<template>
	<ui-root v-if="loaded" :data="smsAccount" :part="form" ref="frm" :errs="errors" :actions="dialogControls(env)"></ui-root>
</template>

<script setup lang="ts">
import {onMounted, onUnmounted, ref, watch, reactive} from "vue";
import {connectEventBus, injectEnv} from "@fd/Environment";
import type {SmsAccount} from "@t/sms/SmsAccount";
import type {WithMeta} from "@t/fd/WithMeta";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition";
import type {ErrorMap} from "@fd/PartInjectKeys";
import {NetworkResponse} from "@fd/Types";
import {useRouter,useRoute} from "vue-router";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {getIntAttParam} from "@/router/Router.ts";
import {useVirtualRoute} from "@/router/virtual/VirtualRouter.ts";

const env = injectEnv();
const navStore = useNavigationStore();
const router = useRouter();
const route = useVirtualRoute();

const loaded = ref<boolean>(false);

const errors = reactive<ErrorMap>([]);
const smsAccount = ref<SmsAccount|null>(null);
const form = ref<InterfaceRoot<FormDefinition>|null>(null);
const bus = connectEventBus("SMSAccountPage");
const frm = ref(null);

function receivedData(data:SmsAccount & WithMeta):void {
	if (data.META) {
		form.value = data.META.form;
		delete data.META;
	}
	console.log("data:"+JSON.stringify(data));
	smsAccount.value = data;
	loaded.value = true;
	if (smsAccount.value && smsAccount.value.id>0) {
		navStore.replace(smsAccount.value.name);
	}
}

function loadData() {
	let endpoint = null;
	const id = route.query.id;
	if (id > 0) {
		endpoint = "api/smsaccounts/"+id+"?meta=true";
	}
	else {
		endpoint = "api/smsaccounts/init?meta=true";
	}
	return env.net.fetch(endpoint)
			.then((result) => result.json())
			.then(receivedData);
}

function saveData():Promise<NetworkResponse> {
    return env.net.fetch("api/smsaccounts/"+smsAccount.value.id, "PUT", smsAccount.value);
}

function createData():Promise<NetworkResponse> {
    return env.net.fetch("api/smsaccounts", "POST", smsAccount.value);
}

function sendData():Promise<NetworkResponse> {
	return (!smsAccount.value.id || smsAccount.value.id===0)?createData():saveData();
}

bus.onCommand(["save", "saveAndBack"], async (cmd) => {
	errors.length = 0;
	const response = await sendData();
	if (response.ok) {
		if (cmd === "save") {
			const data = await response.json();
			receivedData(data);
			if (frm.value) {
				frm.value.setDirty(false);
			}
		} else {
			// Go back
			router.back();
		}
		return "ok";
	} else {
		const data = await response.json();
		if (Array.isArray(data?.problems)) {
			for (let problem of data.problems) {
				errors.push(problem);
			}
		}
		console.error(response);
		return "fail";
	}
});

watch(() => route.query.id, loadData, { immediate: true });
navStore.defineMenu((mb) => mb.standardMenu());

onMounted(() => {
	navStore.forward(env.msg("menu.createSMSAccount"));
});
</script>

<style>

</style>

